import dayjs from "dayjs"
import { getSlots } from "../../../../../_shared/toolbox"

export function generateNewSlots(days, locations, eventType) {
    return days.reduce((acc, day) => {
        let slots = []
        locations.forEach((location) => {
            slots = [
                ...slots,
                ...getSlots(
                    day.date,
                    day.startTime,
                    day.endTime,
                    location.code,
                    eventType.id,
                    eventType.slotDuration
                ),
            ]
        })
        return [...acc, ...slots]
    }, [])
}

export function computeSlots({
    eventType,
    eventTypes,
    days,
    locations,
    slots,
    oldSlots,
    oldDays,
    oldLocations,
    changedField,
    slotIndex,
    extraPivots,
}) {
    if (
        !eventType ||
        !eventTypes ||
        !days?.length ||
        !locations?.length ||
        !changedField
    ) {
        return [[], []]
    }

    const typeId = eventType.id ? eventType.id : eventType
    const type = eventTypes?.find((t) => t.id === typeId)

    if (!oldSlots?.length || changedField === "eventType") {
        const newSlots = generateNewSlots(days, locations, type)
        return [newSlots, []]
    }

    if (changedField === "days") {
        let newSlots = [...oldSlots]
        const toRemove = oldDays.filter(
            (oldDay) => !days.some((newDay) => isSameDay(oldDay, newDay))
        )
        const toCreate = days.filter(
            (newDay) => !oldDays.some((oldDay) => isSameDay(oldDay, newDay))
        )
        toRemove.forEach((oldDay) => {
            newSlots = newSlots.filter((slot) => isSlotOnDay(slot, oldDay))
        })
        toCreate.forEach((newDay) => {
            newSlots = [
                ...newSlots,
                ...generateNewSlots([newDay], locations, type),
            ]
        })

        return [newSlots, []]
    }

    if (changedField === "locations") {
        let newSlots = [...oldSlots]
        const toRemove = oldLocations.filter(
            (oldLocation) =>
                !locations.some((newLocation) =>
                    isSameLocation(oldLocation, newLocation)
                )
        )
        const toCreate = locations.filter(
            (newLocation) =>
                !oldLocations.some((oldLocation) =>
                    isSameLocation(oldLocation, newLocation)
                )
        )
        toRemove.forEach((oldLocation) => {
            newSlots = newSlots.filter(
                (slot) => slot.locationCode === oldLocation.code
            )
        })
        toCreate.forEach((newLocation) => {
            newSlots = [
                ...newSlots,
                ...generateNewSlots(days, [newLocation], type),
            ]
        })

        return [newSlots, []]
    }

    if (changedField === "slotEventType") {
        const slot = oldSlots[slotIndex]
        const day = days.find((day) => isSlotOnDay(slot, day))
        const location = locations.find(
            (location) => location.code === slot.locationCode
        )
        const pivots = slots.filter((pivotCandidate) => {
            const key = pivotCandidate.id || pivotCandidate.tempId
            const hasBlockingConfiguration =
                pivotCandidate.eventType !== typeId ||
                pivotCandidate.booker ||
                extraPivots.includes(key)

            return (
                pivotCandidate.locationCode === location.code &&
                isSlotOnDay(pivotCandidate, day) &&
                hasBlockingConfiguration
            )
        })

        const filteredSlots = slots.filter((s) => {
            return s.locationCode !== location.code && !isSlotOnDay(s, day)
        })

        const [pivotSlots, toManuallyReconnect] = generatePivotSlots(
            pivots,
            day,
            location,
            type,
            eventTypes,
            slots,
            oldSlots
        )

        if (!pivotSlots.length) {
            const newSlots = [
                ...filteredSlots,
                ...getSlots(
                    day.date,
                    day.startTime,
                    day.endTime,
                    location.code,
                    type.id,
                    type.slotDuration
                ),
            ]

            return [newSlots, []]
        }

        const computedSlots = [...filteredSlots, ...pivotSlots]
        return [computedSlots, toManuallyReconnect]
    }
}

export function isSameDay(d1, d2) {
    return ["date", "startTime", "endTime"].every((f) => d1[f] === d2[f])
}

export function isSlotOnDay(slot, day) {
    const [slotDate] = getSlotStartEnd(slot)
    const [dayStart, dayEnd] = toStartEnd(day.date, day.startTime, day.endTime)
    if (slotDate.isSame(dayStart, "second")) {
        return true
    }

    if (slotDate.isAfter(dayStart) && slotDate.isBefore(dayEnd)) {
        return true
    }

    return false
}

export function isSameLocation(l1, l2) {
    return l1.code === l2.code
}

export function toStartEnd(date, start, end) {
    const [hourStart, minuteStart] = start.split(":")
    const [hourEnd, minuteEnd] = end.split(":")
    const startParsed = dayjs(date)
        .set("hour", hourStart)
        .set("minute", minuteStart)
    const endParsed = dayjs(date).set("hour", hourEnd).set("minute", minuteEnd)
    return [startParsed, endParsed]
}

export function getSlotStartEnd(slot) {
    const { date, window } = slot
    const [startTime, endTime] = window.split(" - ")
    return toStartEnd(date, startTime, endTime)
}

export function isSameOrBefore(d1, d2, unit = "minutes") {
    d1 = dayjs(d1)
    d2 = dayjs(d2)
    return d1.isSame(d2, unit) || d1.isBefore(d2, unit)
}

export function hasOverlap(d1, d2) {
    const [start1, end1] = toStartEnd(d1.date, d1.startTime, d1.endTime)
    const [start2, end2] = toStartEnd(d2.date, d2.startTime, d2.endTime)

    if (!start1.isSame(start2, "day")) {
        return false
    }

    if (start1.isSame(start2, "second") || end1.isSame(end2, "second")) {
        return true
    }

    if (start1.isAfter(start2) && start1.isBefore(end2)) {
        return true
    }

    if (end1.isAfter(start2) && end1.isBefore(end2)) {
        return true
    }

    return false
}

export function pivotSort(slot1, slot2) {
    const [s1] = getSlotStartEnd(slot1)
    const [s2] = getSlotStartEnd(slot2)
    return s1.unix() - s2.unix()
}

function reconnectSlots(oldSlots, newSlots) {
    const toManuallyReconnect = []
    const knownIds = new Set(newSlots.map((s) => s.id || s.tempId))
    function tryReconnectSlot(oldSlot) {
        const {
            id,
            tempId,
            window,
            date,
            locationCode,
            note,
            booker,
            agentReservation,
            visible,
            eventType,
        } = oldSlot

        if (knownIds.has(id || tempId)) {
            return
        }

        const i = newSlots.findIndex(
            (newSlot) =>
                newSlot.date === date &&
                newSlot.window === window &&
                newSlot.locationCode === locationCode &&
                newSlot.eventType === eventType
        )
        if (i > -1) {
            newSlots[i] = {
                ...newSlots[i],
                id,
                tempId,
                note,
                booker,
                agentReservation,
                visible,
            }
        } else if (note || booker || agentReservation) {
            toManuallyReconnect.push(oldSlot)
        }
    }
    oldSlots.forEach((oldSlot) => {
        tryReconnectSlot(oldSlot)
    })
    return [newSlots, toManuallyReconnect]
}

function generatePivotSlots(
    pivots,
    day,
    location,
    mainType,
    eventTypes,
    oldSlots
) {
    let newSlots = []
    pivots.sort(pivotSort)
    pivots.forEach((pivot, index) => {
        const pivotType = eventTypes.find((type) => type.id === pivot.eventType)
        let [pivotStart, pivotEnd] = getSlotStartEnd(pivot)
        pivotEnd = dayjs(pivotStart).add(pivotType.slotDuration, "minutes")

        if (index === 0) {
            newSlots = getSlots(
                day.date,
                day.startTime,
                pivotStart.format("HH:mm"),
                location.code,
                mainType.id,
                mainType.slotDuration
            )
        }

        const nextPivot = pivots[index + 1]

        if (nextPivot) {
            let [nextPivotStart] = getSlotStartEnd(nextPivot)

            newSlots = [
                ...newSlots,
                {
                    ...pivot,
                    window: `${pivotStart.format("HH:mm")} - ${pivotEnd.format(
                        "HH:mm"
                    )}`,
                },
                ...getSlots(
                    day.date,
                    pivotEnd.format("HH:mm"),
                    nextPivotStart.format("HH:mm"),
                    location.code,
                    mainType.id,
                    mainType.slotDuration
                ),
            ]
        } else {
            newSlots = [
                ...newSlots,
                {
                    ...pivot,
                    window: `${pivotStart.format("HH:mm")} - ${pivotEnd.format(
                        "HH:mm"
                    )}`,
                },
                ...getSlots(
                    day.date,
                    pivotEnd.format("HH:mm"),
                    day.endTime,
                    location.code,
                    mainType.id,
                    mainType.slotDuration
                ),
            ]
        }
    })

    return reconnectSlots(oldSlots, newSlots)
}
